/**
 * 보드 위 도구줄 — 뷰 전환과 필터.
 *
 * **원칙은 카드와 같다: 안 정한 것은 안 그린다.** 옛 판은 네이티브 선택기
 * 넷이 항상 서 있었고 그중 대부분이 「아직 아무것도 안 고름」이었다 —
 * 좁은 화면에서 도구줄이 세 줄이 됐고, 두 줄이 빈 선택기였다.
 *
 * 지금은 **걸린 것만 칩으로 자란다.** 아무것도 안 걸렸으면 도구줄은
 * 뷰 전환 · 「내 작업」 · 「내가 낸 것」 · 「필터」뿐이다.
 *
 * ⚠ **「새로고침」 단추를 걷었다**(2026-08-26 지시). 보드는 필터를 바꾸거나
 * 티켓을 만들 때마다 스스로 다시 부른다 — 사람이 눌러야 하는 자리가 아니다.
 */

import { Button } from '@/components/ui/button';

import type { Label, Project, TicketFilter, UserBrief } from '../api';
import { ProjectPicker } from '../ProjectPicker';
import { FilterChips } from './FilterChips';
import { FilterMenu } from './FilterMenu';
import { SavedViews } from './SavedViews';

export type BoardView = 'board' | 'table' | 'list';

const VIEWS: { id: BoardView; label: string }[] = [
  { id: 'board', label: '보드' },
  { id: 'table', label: '표' },
  { id: 'list', label: '목록' },
];

/**
 * 보류 축의 걸음 셋. **단추는 하나이고 눌러서 돈다**(2026-09-02).
 *
 * 값이 셋이라 다른 단추들처럼 켜고 끄는 것으로는 못 담는다. 그렇다고
 * 단추를 셋 세우면 도구줄에 **늘 서 있는 손잡이가 셋 늘어난다** — 이
 * 도구줄이 옛 선택기 넷을 걷어낸 이유가 그것이었다(파일 머리 주석).
 * 세그먼트 한 벌도 폭은 단추 셋과 같다.
 *
 * 팝오버로 빼는 길은 막혀 있다 — 「팝오버는 하나뿐이다」가 `FilterMenu`
 * 의 규칙이고, 축마다 레이어를 두면 좁은 화면에서 화면 밖으로 밀린다.
 *
 * ⚠ **글자가 「지금 무엇을 보고 있나」를 말한다.** 이름을 고정하고 색으로만
 * 켜짐을 말하면 걸린 두 상태(숨김·만)가 똑같아 보인다.
 *
 * 자주 쓰는 것이 먼저다 — 「숨김」이 보드를 살리는 손잡이이고, 「만」은
 * 미뤄 둔 것을 **되돌리러 가는** 길이다. 되돌릴 길이 화면에 없으면
 * 사람이 미루기를 무서워한다.
 */

export function BoardToolbar({
  projects,
  projectId,
  onProjectChange,
  view,
  onView,
  filter,
  onFilter,
  members,
  labels,
  me,
  filtered,
  canCreate,
  onCreate,
  summary,
}: {
  projects: Project[];
  projectId: number | null;
  onProjectChange: (id: number) => void;
  view: BoardView;
  onView: (next: BoardView) => void;
  filter: TicketFilter;
  onFilter: (next: TicketFilter) => void;
  members: UserBrief[];
  labels: Label[];
  me: number;
  filtered: boolean;
  /** 만들 수 있나. **막는 것은 서버다** — 여기서는 안 그릴 뿐이다. */
  canCreate: boolean;
  onCreate: () => void;
  /** 보드 위 숫자 칸(`BoardSummary`). 도구줄 두 줄 사이에 선다. */
  summary?: React.ReactNode;
}) {
  /* 걸린 축의 수. 필터 단추에 붙여 「무언가 걸려 있다」를 닫힌 채로 말한다. */
  const active = Object.values(filter).filter((v) => v != null && v !== '').length;

  return (
    <div className="board-toolbar">
      {/* **첫 줄: 어디를 어떻게 보나**(2026-10-18 C 현황판형). 프로젝트 ·
          보기 탭 · 새 티켓. 옛 판은 한 상자 안에 단추 열 개가 섞여 있어
          「보기를 바꾸는 손잡이」와 「거르는 손잡이」가 안 갈렸다. */}
      <div className="board-toolbar-top">
        <span className="flex items-center gap-s3">
          <ProjectPicker
            projects={projects}
            projectId={projectId}
            onChange={onProjectChange}
          />
        </span>
        <div
          className="view-tabs"
          role="group"
          aria-label="보기 방식"
        >
          {VIEWS.map((item) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={view === item.id}
              onClick={() => onView(item.id)}
              className="max-[720px]:min-h-[44px]"
            >
              {item.label}
            </button>
          ))}
        </div>
        {canCreate && (
          <span className="ml-auto">
          <Button
            type="button"
            size="sm"
            className="max-[720px]:h-[44px]"
            onClick={onCreate}
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
              <path
                d="M6.5 2.5v8M2.5 6.5h8"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
            새 티켓
          </Button>
          </span>
        )}
      </div>

      {summary}

      {/* 둘째 줄: 거르는 손잡이만. */}
      <div className="board-toolbar-filters">
        {/*
          **만들기는 이 줄의 첫 자리다**(2026-08-29 지시: 「공통 생성 버튼이
          필요하다」). 옛 판은 칸 머리마다 `+` 가 하나씩 있었다 — 이름과 개수
          옆에 단추가 끼어 칸 머리가 세 조각으로 갈렸고, **표·목록 뷰에는
          만드는 자리가 아예 없었다.**

          단추 하나로 모으면서 어느 칸에 설지는 폼이 묻는다.
        */}

        {/* 뷰 전환 — 세그먼트 하나. `role="tablist"` 를 안 쓴다: 탭 패널
            의미가 아니라 **같은 자료의 표현을 고르는 것**이고, 아래 내용은
            탭 내용이 아니라 페이지 본문이다. */}

        {/* 「내 작업」·「내가 낸 것」·「보류」는 **필터 안으로 들어갔다**(2026-10-19
            사용자 지시). 도구줄에 단추 셋이 따로 서 있으니 거르는 손잡이가 두
            군데로 갈렸다 — 이제 담당자·작성자·보류 묶음에서 고른다. */}

        <FilterMenu
          filter={filter}
          onFilter={onFilter}
          members={members}
          labels={labels}
          me={me}
        >
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="max-[720px]:h-10 max-[720px]:rounded-full max-[720px]:px-s4"
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
              <path
                d="M1.5 2.5h10M3.5 6.5h6M5.5 10.5h2"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
            </svg>
            필터
            {active > 0 && <span className="tnum font-bold text-brand">{active}</span>}
          </Button>
        </FilterMenu>

        {/* 저장된 뷰 — 필터 축이 여덟이라 자주 쓰는 조합을 매번 다시
            조립하는 것이 남은 마찰이었다. **필터 바로 옆이 자리다** —
            거는 손잡이와 불러오는 손잡이가 떨어져 있으면 둘이 같은 축을
            건드린다는 것이 안 읽힌다.

            아무것도 저장 안 했고 걸린 필터도 없으면 **아무것도 안 그린다**
            (이 도구줄의 원칙 그대로). */}
        {/* `key` 로 마디를 새로 세운다 — 프로젝트가 바뀌면 담긴 뷰도
            통째로 다른 것이다(그 안 주석 참고). */}
        <SavedViews
          key={projectId}
          projectId={projectId}
          filter={filter}
          onFilter={onFilter}
        />

      </div>

      {/*
        걸린 필터. **없으면 이 줄이 통째로 사라진다** — 빈 줄이 남으면
        도구줄이 늘 두 줄로 보이고, 필터가 걸렸다는 신호가 죽는다.
      */}
      {filtered && (
        <div className="mt-s3 flex flex-wrap items-center gap-s2 border-t border-line pt-s3">
          <FilterChips
            filter={filter}
            onFilter={onFilter}
            members={members}
            labels={labels}
            me={me}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-ink-3 max-[720px]:h-[44px]"
            onClick={() => onFilter({})}
          >
            모두 지우기
          </Button>
          {/* **필터 중에는 순서를 못 정한다는 것을 말해야 한다.** 안 보이는
              카드가 사이에 있어서 이웃 기준 계산이 어긋난다 — 화면이 그것을
              안 알리면 「끌어다 놨는데 엉뚱한 데 간다」로만 나타난다. */}
          {view === 'board' && (
            <span className="ml-auto text-small text-ink-3 max-[720px]:hidden">
              필터 중에는 순서를 정할 수 없다 — 옮긴 카드는 칸 맨 끝에 붙는다
            </span>
          )}
        </div>
      )}
    </div>
  );
}
